<template>
  <div class="wallet-content manager">
    <LoaderOverlay v-if="loading" />

    <div v-else>
      <!-- État d'accueil pour les nouveaux utilisateurs -->
      <EmptyState
        v-if="allTickets.length === 0"
        variant="welcome"
        emptyMessage="Bienvenue! 🎉"
      >
        <template #description>
          <p>
            Vous n'avez pas encore de tickets, dès que vous réserverez votre premier trajet, vous retrouverez tous vos tickets ici.
          </p>
        </template>
        <template #actions>
          <div class="btn-group">
            <router-link to="/search" class="btn btn-primary">
              🚗 Rechercher un trajet
            </router-link>
            <router-link to="/help" class="btn btn-secondary">
              En savoir plus
            </router-link>
          </div>
        </template>
      </EmptyState>

      <!-- Contenu normal si l'utilisateur a des tickets -->
      <template v-else>
        <!-- Filtres -->
        <section class="filters form-group">
          <div class="btn-group sb">
            <div class="group-rows">
              <label>Filtres</label>
              <button
                @click="toggleFilter('history')"
                class="btn btn-chip"
                :class="{ 'is-active': filters.showHistory }"
              >
                {{ filters.showHistory ? 'Masquer l\'historique' : 'Voir tout l\'historique' }}
              </button>

              <select
                id="status-filter"
                v-model="filters.status"
                class="input input--sm"
              >
                <option value="">-- Sélectionner un statut --</option>
                <option value="pending_payment">En attente</option>
                <option value="paid">Payé</option>
                <option value="validated">Validé</option>
                <option value="cancelled">Annulé</option>
                <option value="refunded">Remboursé</option>
              </select>
            </div>

            <div v-if="hasActiveFilters" class="align-right">
              <button @click="resetFilters" class="btn btn-secondary">
                Réinitialiser
              </button>
            </div>
          </div>
        </section>

        <p class="results-count">
          {{ filteredTickets.length }} {{ ticketsLabel }}
          <span v-if="hasActiveFilters" class="badge badge-info">
            {{ activeFiltersCount }} filtre(s) actif(s)
          </span>
        </p>

        <!-- Tickets groupés par date -->
        <section v-if="Object.keys(ticketsByDate).length > 0">
          <div v-for="(tickets, dateKey) in ticketsByDate" :key="dateKey" class="date-group">
            <h3
              class="date-header"
              :class="{ 'is-collapsed': !expandedDates[dateKey] }"
              @click="toggleDate(dateKey)"
              role="button"
              tabindex="0"
              @keydown.enter.prevent="toggleDate(dateKey)"
              @keydown.space.prevent="toggleDate(dateKey)"
            >
              <span class="date-header__icon">
                {{ expandedDates[dateKey] ? '▼' : '▶' }}
              </span>
              <span class="date-header__text">{{ formatDateHeader(dateKey) }}</span>
              <span class="date-header__count">{{ tickets.length }}</span>
            </h3>

            <transition name="expand">
              <div v-show="expandedDates[dateKey]" class="listing-wrapper">
                <div class="listing" role="table" aria-colcount="3">
                  <div class="listing__body" role="rowgroup">
                    <TicketRow v-for="ticket in tickets" :key="ticket.uuid" :ticket="ticket" />
                  </div>
                </div>
              </div>
            </transition>
          </div>
        </section>

        <!-- Message si aucun ticket ne correspond aux filtres -->
        <EmptyState
          v-else
          :is-empty="true"
          emptyMessage="Aucun ticket ne correspond à vos critères."
        >
          <template #actions>
            <button @click="resetFilters" class="btn btn-secondary">
              Réinitialiser les filtres
            </button>
          </template>
        </EmptyState>
      </template>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useUserStore } from '@/stores/userStore'
import LoaderOverlay from '@/components/LoaderOverlay.vue'
import EmptyState from '@/components/EmptyState.vue'
import TicketRow from '@/components/TicketRow.vue'

const userStore = useUserStore()
const loading = ref(false)

const STORAGE_KEY = 'ticketFilters'

// État des dates expandues/collapsées
const expandedDates = ref({})

// Charger les filtres depuis sessionStorage
function loadFiltersFromSession() {
  const saved = sessionStorage.getItem(STORAGE_KEY)
  if (saved) {
    try {
      return JSON.parse(saved)
    } catch (e) {
      console.error('Erreur lors du chargement des filtres:', e)
    }
  }
  return {
    status: '',
    showHistory: false
  }
}

// Sauvegarder les filtres dans sessionStorage
function saveFiltersToSession() {
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(filters.value))
}

const filters = ref(loadFiltersFromSession())

// Tickets de base (tous les tickets)
const allTickets = computed(() => userStore.user?.tickets || [])

const hasActiveFilters = computed(() => {
  return !!(filters.value.status || filters.value.showHistory)
})

const activeFiltersCount = computed(() => {
  let count = 0
  if (filters.value.status) count++
  if (filters.value.showHistory) count++
  return count
})

function toggleFilter(filterType) {
  if (filterType === 'history') {
    filters.value.showHistory = !filters.value.showHistory
  }
}

// Watcher pour sauvegarder les filtres à chaque changement
watch(filters, () => {
  saveFiltersToSession()
}, { deep: true })

const filteredTickets = computed(() => {
  let result = [...allTickets.value]

  // Filtre temporel : mois en cours + à venir (sauf si historique activé)
  if (!filters.value.showHistory) {
    const now = new Date()
    const startOfCurrentMonth = new Date(now.getFullYear(), now.getMonth(), 1)
    startOfCurrentMonth.setHours(0, 0, 0, 0)

    result = result.filter(ticket => {
      const tripDate = new Date(ticket.trip.timeToStart)
      // Garder les tickets du mois en cours + tous ceux à venir
      return tripDate >= startOfCurrentMonth
    })
  }

  // Filtre par status avec logique exclusive
  if (filters.value.status) {
    const statusFilter = filters.value.status.toLowerCase()

    result = result.filter(ticket => {
      const isValidated = ticket.validatedAt !== null && ticket.validatedAt !== undefined
      const ticketStatus = ticket.status.toLowerCase()

      // Logique de filtrage exclusive :
      switch (statusFilter) {
        case 'validated':
          // Uniquement les tickets validés
          return isValidated

        case 'paid':
          // Uniquement les tickets payés MAIS pas encore validés
          return ticketStatus === 'paid' && !isValidated

        case 'pending_payment':
          // Uniquement en attente de paiement (donc forcément pas validé)
          return ticketStatus === 'pending_payment'

        case 'cancelled':
        case 'refunded':
          // Annulé ou remboursé
          return ticketStatus === statusFilter

        default:
          return false
      }
    })
  }

  // Tri par date décroissante
  return result.sort((a, b) => {
    return new Date(b.trip.timeToStart) - new Date(a.trip.timeToStart)
  })
})

// Grouper les tickets par date
const ticketsByDate = computed(() => {
  return filteredTickets.value.reduce((groups, ticket) => {
    const date = new Date(ticket.trip.timeToStart)
    const dateKey = date.toISOString().split('T')[0] // Format YYYY-MM-DD

    if (!groups[dateKey]) {
      groups[dateKey] = []
    }

    groups[dateKey].push(ticket)
    return groups
  }, {})
})

// Fonction pour toggler une date
function toggleDate(dateKey) {
  expandedDates.value[dateKey] = !expandedDates.value[dateKey]
}

// Initialiser les dates expandues (seule la plus récente ouverte)
function initializeExpandedDates() {
  const dates = Object.keys(ticketsByDate.value)

  if (dates.length === 0) return

  // Réinitialiser
  expandedDates.value = {}

  // Ouvrir uniquement la première date (la plus récente)
  dates.forEach((date, index) => {
    expandedDates.value[date] = index === 0
  })
}

// Watcher pour réinitialiser quand les tickets changent
watch(ticketsByDate, () => {
  initializeExpandedDates()
}, { immediate: true })

// Fonction pour formater l'en-tête de date
function formatDateHeader(dateString) {
  const date = new Date(dateString)
  const today = new Date()
  const tomorrow = new Date(today)
  tomorrow.setDate(tomorrow.getDate() + 1)
  const yesterday = new Date(today)
  yesterday.setDate(yesterday.getDate() - 1)

  // Normaliser les dates pour la comparaison (ignorer l'heure)
  const normalizeDate = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate())
  const normalizedDate = normalizeDate(date)
  const normalizedToday = normalizeDate(today)
  const normalizedTomorrow = normalizeDate(tomorrow)
  const normalizedYesterday = normalizeDate(yesterday)

  if (normalizedDate.getTime() === normalizedToday.getTime()) {
    return "Aujourd'hui"
  } else if (normalizedDate.getTime() === normalizedTomorrow.getTime()) {
    return "Demain"
  } else if (normalizedDate.getTime() === normalizedYesterday.getTime()) {
    return "Hier"
  }

  return date.toLocaleDateString('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  })
}

const ticketsLabel = computed(() =>
  filteredTickets.value.length > 1 ? 'tickets trouvés' : 'ticket trouvé'
)

function resetFilters() {
  filters.value = {
    status: '',
    showHistory: false
  }
}
</script>

<style scoped>
.date-group {
  margin-bottom: 0.5rem;
}

.date-header {
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--color-text-primary);
  margin-bottom: 0;
  padding: 0.75rem 1rem;
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-md);
  text-transform: capitalize;
  cursor: pointer;
  user-select: none;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background: var(--color-grey-light);
  transition: all 0.2s ease;
}

.date-header:hover {
  background: var(--bg-black-alpha);
  border-color: var(--color-feuille);
}

.date-header:focus {
  outline: none;
  border-color: var(--color-feuille);
  box-shadow: var(--shadow-input);
}

.date-header.is-collapsed {
  margin-bottom: 0;
  border-radius: var(--border-radius-md);
}

.date-header__icon {
  font-size: 0.75rem;
  transition: transform 0.2s ease;
  flex-shrink: 0;
  color: var(--color-text-secondary, #6b7280);
}

.date-header__text {
  flex: 1;
}

.date-header__count {
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--color-text-secondary, #6b7280);
  background: var(--color-bg-tertiary, #fff);
  padding: 0.25rem 0.625rem;
  border-radius: var(--border-radius-lg);
  flex-shrink: 0;
}

.listing-wrapper {
  overflow: hidden;
  padding-top: 0.5rem;
}

/* Animation d'expansion/collapse */
.expand-enter-active,
.expand-leave-active {
  transition: all 0.3s ease-in-out;
  max-height: 2000px;
}

.expand-enter-from,
.expand-leave-to {
  opacity: 0;
  max-height: 0;
  padding-top: 0;
}

.btn-group a.btn-primary {
  color: var(--bg-primary);
}

@media (max-width: 768px) {
  .date-header {
    font-size: 1rem;
    padding: 0.625rem 0.75rem;
  }

  .date-header__count {
    font-size: 0.8125rem;
  }

  .filters__grid {
    grid-template-columns: 1fr;
  }

  .period__buttons {
    justify-content: flex-start;
    flex-direction: column;
    align-items: flex-start;
  }

  .period__buttons > div {
    width: 100%;
  }

  .btn-group {
    flex-flow: column wrap;
  }

  .group-rows select,
  .group-rows .btn-chip {
    flex: 1 1 auto;
  }
}
</style>
